import React, { useState } from 'react';
import { MoreVertical } from 'lucide-react';
import { format } from 'date-fns';
import notesIcon from '../../assets/notes.svg';
import Notepad from './notepad';
import singleError from '../../assets/singleError.svg';

interface Note {
  title: string;
  description: string;
  status: string;
  date: string;
}

interface NotesCardProps {
  notes: Note[];
  className?: string;
  onAddNote?: (note: Note) => Promise<boolean>;
}
  
const NotesCard: React.FC<NotesCardProps> = ({ notes, className = '', onAddNote }) => {
  const [selectedNote, setSelectedNote] = useState<number | null>(null);
  const [isNotepadOpen, setIsNotepadOpen] = useState(false);

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'in progress':
        return 'bg-[#E3F2FD] text-[#1565C0]';
      default:
        return 'bg-[#E3F2FD] text-[#1565C0]';
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return format(date, 'dd MMM yy');
    } catch (error) {
      return dateString;
    }
  };

  const handleSaveNote = async (note: { title: string; description: string; status: string }) => {
    const newNote = {
      ...note,
      date: new Date().toISOString(),
    };
    
    if (onAddNote) {
      return await onAddNote(newNote);
    }
    return true;
  };

  return (
    <div className={`flex flex-col items-start w-full relative ${className}`}>
      <div className="flex items-center justify-between w-full mb-4">
        <div className="text-[20px] font-semibold text-[#222222]">Notes</div>
        <button 
          className="flex items-center w-fit justify-center gap-2 px-2 py-1 rounded-[8px] text-[#000000] transition-all hover:bg-[#FAFAFA] border border-[#EEEEEE]"
          onClick={() => setIsNotepadOpen(true)}
        >
          <img src={notesIcon} alt="Notes" className="w-[14px] h-[14px] text-[#7A7A7A]" />
          <span className="text-[14px] font-[400]">Create notes</span>
        </button>
      </div>

      <div className="w-full h-full space-y-4">
        {notes.map((note, index) => (
          <div 
            key={index}
            className="flex flex-col gap-2 p-4 bg-[#FFFFFF] border border-[#EEEEEE] rounded-lg cursor-pointer hover:shadow-sm transition-shadow"
          >
            <div className="flex items-start justify-between">
              <h3 className="text-[16px] font-medium text-[#222222]">
                {note.title}
              </h3>
              <button className="p-1 hover:bg-[#FAFAFA] rounded-full">
                <MoreVertical className="w-4 h-4 text-[#7A7A7A]" />
              </button>
            </div>

            <p className="text-[14px] text-[#7A7A7A] font-normal">
              {note.description}
            </p>

            <div className="flex items-center justify-between mt-2">
              <span className={`px-2 py-1 rounded-[8px] text-xs font-normal ${getStatusColor(note.status)}`}>
                {note.status}
              </span>
              <span className="text-xs text-[#7A7A7A]">
                {formatDate(note.date)}
              </span>
            </div>
          </div>
        ))}

        {notes.length === 0 && (
          <div className="flex flex-col h-full items-center justify-center py-8 text-center gap-3">
            <img src={singleError} alt="Single Error" className="w-[160px] h-[34px] text-[#7A7A7A]" />
            <div className="flex flex-col items-center justify-center gap-1">
              <span className="text-[16px] font-semibold text-[#222222]">No notes available</span>
              <span className="text-[14px] font-medium text-[#7A7A7A]">Create a note to get started</span>
            </div>
          </div>
        )}
      </div>

      {isNotepadOpen && (
        <Notepad
          isOpen={isNotepadOpen}
          onClose={() => setIsNotepadOpen(false)}
          onSave={handleSaveNote}
        />
      )}
    </div>
  );
};

export default NotesCard;
